import { useCallback, useEffect, useState } from 'react';
import {
  fetchServerAppVersion,
  setDismissedUpdateVersion,
  shouldShowUpdateForVersion
} from '@/utils/appVersion';

const POLL_INTERVAL_MS = 5 * 60 * 1000;

const useAppUpdateAvailable = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [serverVersion, setServerVersion] = useState<string | null>(null);

  const checkForUpdate = useCallback(async () => {
    const latest = await fetchServerAppVersion();
    if (!latest) return;

    setServerVersion(latest);
    setUpdateAvailable(shouldShowUpdateForVersion(latest));
  }, []);

  useEffect(() => {
    void checkForUpdate();

    const intervalId = window.setInterval(() => {
      void checkForUpdate();
    }, POLL_INTERVAL_MS);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void checkForUpdate();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [checkForUpdate]);

  const dismissUpdate = useCallback(() => {
    if (serverVersion) {
      setDismissedUpdateVersion(serverVersion);
    }
    setUpdateAvailable(false);
  }, [serverVersion]);

  return {
    updateAvailable,
    serverVersion,
    checkForUpdate,
    dismissUpdate
  };
};

export default useAppUpdateAvailable;
